import React, {
  forwardRef,
  ForwardRefRenderFunction,
  useCallback,
  useEffect,
  useImperativeHandle,
} from 'react';
import classNames from 'classnames';
import useCallbackRef from 'hooks/useCallbackRef';
import { OverlayRef } from 'ncoded-component-library/build/components/atoms/Overlay/Overlay.component';
import { ModalProps } from 'ncoded-component-library/build/components/organisms/Modal/Modal.component';
import FilePreviewModal from 'components/FilePreviewModal';
import { isImage, isPdf, isVideo } from 'utils';
import useFilePreviewUrlQuery from 'hooks/useFilePreviewUrlQuery';
import useFileContentQuery from 'hooks/useFileContentQuery';
import useObjectUrl from 'hooks/useObjectUrl';
import useDownloadFile from 'hooks/useDownloadFile';
import { MessageFileHandlers } from '../MessageFile/MessageFile.component';

import './MessageFilePreviewModal.styles.scss';

type MessageFilePreviewModalProps = {
  fileId: string;
  fileName?: string;
  /** owning resource (chat session / note) — keeps ids of different resources apart */
  scope?: string;
  onPrev?: () => void;
  onNext?: () => void;
} & Pick<MessageFileHandlers, 'onFilePreview'> &
  ModalProps;

const MessageFilePreviewModal: ForwardRefRenderFunction<
  OverlayRef,
  MessageFilePreviewModalProps
> = (props, ref) => {
  const {
    className,
    fileId,
    fileName,
    scope,
    onFilePreview,
    onPrev,
    onNext,
    ...rest
  } = props;

  const classes = classNames('message-file-preview-modal', className);

  const [modal, modalRef] = useCallbackRef<OverlayRef>();

  useImperativeHandle(ref, () => modal, [modal]);

  const isModalOpen = modal?.openState;

  const { data, isFetching, isSuccess, isError } = useFilePreviewUrlQuery({
    fileId,
    scope,
    fetchPreview: onFilePreview,
    enabled: !!isModalOpen,
  });

  const { url, type } = data || {};

  // cache the actual file bytes for browser-rendered types so re-opening the same file
  // (or arrowing back to it) doesn't re-download it.
  const isBrowserRenderable = isPdf(type) || isImage(type) || isVideo(type);

  const {
    data: fileBlob,
    isFetching: isContentFetching,
    isError: isContentError,
  } = useFileContentQuery({
    id: fileId,
    url,
    enabled: !!isModalOpen && isBrowserRenderable && !!url,
  });

  const objectUrl = useObjectUrl(fileBlob);

  // fall back to the signed url if byte caching fails (e.g. CORS) so the preview still
  // renders instead of showing nothing.
  const previewUrl = isBrowserRenderable
    ? objectUrl ?? (isContentError ? url : undefined)
    : url;

  const isPreviewFetching =
    isFetching ||
    (isBrowserRenderable &&
      !objectUrl &&
      !isContentError &&
      (isContentFetching || !!fileBlob));

  const downloadFile = useDownloadFile();

  const onDownload = useCallback(
    () => downloadFile({ id: fileId, url, name: fileName }),
    [downloadFile, fileId, url, fileName],
  );

  // nothing to show — the file is gone or the request failed
  useEffect(() => {
    if (!isModalOpen) return;
    if (isError || (isSuccess && !url)) modal?.close();
  }, [isModalOpen, isError, isSuccess, url, modal]);

  return (
    <FilePreviewModal
      ref={modalRef}
      className={classes}
      type={type}
      url={previewUrl}
      isUrlFetching={isPreviewFetching}
      isDownloadDisabled={!url}
      downloadResetKey={fileId}
      onDownload={onDownload}
      onPrev={onPrev}
      onNext={onNext}
      {...rest}
    />
  );
};

export default forwardRef(MessageFilePreviewModal);
